import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Building2,
  Check,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  FileSpreadsheet,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import api from "@/lib/api";
import { CampusBulkDeletionDialog } from "@/components/CampusBulkDeletionDialog";

const emptyForm = {
  name: "",
  code: "",
  bank_name: "",
  bank_account_holder_name: "",
  bank_account_number: "",
};

const CAMPUS_COLLATOR = new Intl.Collator("id", { numeric: true, sensitivity: "base" });

function normalizeCampusSearch(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("id-ID")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function CampusSortableHeader({ column, label, onSort, sortConfig }) {
  const isActive = sortConfig.key === column;
  const direction = isActive ? sortConfig.direction : null;
  const nextAction = direction === "asc" ? "Urutkan menurun" : "Urutkan menaik";
  const Icon = direction === "asc" ? ChevronUp : direction === "desc" ? ChevronDown : ChevronsUpDown;

  return (
    <th className="px-5 py-3 font-semibold">
      <button
        type="button"
        onClick={() => onSort(column)}
        data-testid={`sort-campuses-${column}`}
        aria-label={`${nextAction} berdasarkan ${label}`}
        title={`${nextAction} berdasarkan ${label}`}
        className={[
          "inline-flex items-center gap-1.5 transition-colors",
          isActive ? "text-[#0B6B3A]" : "hover:text-[#1F2937]",
        ].join(" ")}
      >
        {label}
        <Icon className="h-3.5 w-3.5" />
      </button>
    </th>
  );
}

function campusSortValue(campus, key) {
  if (key === "code") return campus.code || "";
  if (key === "bank") return campus.bank_name || "";
  if (key === "holder") return campus.bank_account_holder_name || "";
  if (key === "account") return campus.bank_account_number_masked || "";
  return campus.name || "";
}

export default function CampusManager() {
  const importRef = useRef(null);
  const [campuses, setCampuses] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingCampusId, setEditingCampusId] = useState("");
  const [inlineForm, setInlineForm] = useState(emptyForm);
  const [savingCampusId, setSavingCampusId] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [bulkDeletion, setBulkDeletion] = useState(null);
  const [selectedCampusIds, setSelectedCampusIds] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get("/campuses");
      const nextCampuses = response.data || [];
      setCampuses(nextCampuses);
      setSelectedCampusIds((current) => {
        const availableIds = new Set(nextCampuses.map((campus) => campus.id));
        return current.filter((campusId) => availableIds.has(campusId));
      });
    } catch {
      toast.error("Data kampus belum dapat dimuat.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredCampuses = useMemo(() => {
    const terms = normalizeCampusSearch(query).split(" ").filter(Boolean);
    if (!terms.length) return campuses;
    return campuses.filter((campus) => {
      const searchable = normalizeCampusSearch([
        campus.name,
        campus.code,
        campus.bank_name,
        campus.bank_account_holder_name,
        campus.bank_account_number_masked,
      ].join(" "));
      return terms.every((term) => searchable.includes(term));
    });
  }, [campuses, query]);

  const sortedCampuses = useMemo(() => {
    if (!sortConfig.key) return filteredCampuses;
    return [...filteredCampuses].sort((first, second) => {
      const firstValue = campusSortValue(first, sortConfig.key);
      const secondValue = campusSortValue(second, sortConfig.key);
      const comparison = CAMPUS_COLLATOR.compare(String(firstValue), String(secondValue));
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [filteredCampuses, sortConfig]);

  const allVisibleCampusesSelected = (
    sortedCampuses.length > 0
    && sortedCampuses.every((campus) => selectedCampusIds.includes(campus.id))
  );

  const updateSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const toggleCampusSelection = (campusId) => {
    setSelectedCampusIds((current) => (
      current.includes(campusId)
        ? current.filter((selectedCampusId) => selectedCampusId !== campusId)
        : [...current, campusId]
    ));
  };

  const toggleAllCampusSelections = () => {
    setSelectedCampusIds((current) => {
      if (allVisibleCampusesSelected) {
        const visibleIds = new Set(sortedCampuses.map((campus) => campus.id));
        return current.filter((campusId) => !visibleIds.has(campusId));
      }
      const combinedIds = new Set(current);
      sortedCampuses.forEach((campus) => combinedIds.add(campus.id));
      return [...combinedIds];
    });
  };

  const resetForm = () => {
    setForm(emptyForm);
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post("/campuses", form);
      toast.success("Kampus baru ditambahkan.");
      resetForm();
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal menyimpan data kampus.");
    } finally {
      setSaving(false);
    }
  };

  const startInlineEdit = (campus) => {
    setEditingCampusId(campus.id);
    setInlineForm({
      name: campus.name || "",
      code: campus.code || "",
      bank_name: campus.bank_name || "",
      bank_account_holder_name: campus.bank_account_holder_name || "",
      bank_account_number: "",
    });
  };

  const cancelInlineEdit = () => {
    setEditingCampusId("");
    setInlineForm(emptyForm);
  };

  const saveInlineEdit = async (campus) => {
    if (inlineForm.name.trim().length < 2) {
      toast.error("Nama kampus minimal 2 karakter.");
      return;
    }
    setSavingCampusId(campus.id);
    try {
      await api.put(`/campuses/${campus.id}`, inlineForm);
      toast.success("Data kampus diperbarui.");
      cancelInlineEdit();
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal memperbarui data kampus.");
    } finally {
      setSavingCampusId("");
    }
  };

  const importFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Pilih file Excel berformat XLSX.");
      return;
    }
    setImporting(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await api.post("/campuses/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `${response.data.inserted} kampus ditambahkan, ${response.data.updated} diperbarui.`,
      );
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal mengimpor file kampus.");
    } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/campuses/${deleting.id}`);
      setCampuses((previous) => previous.filter((campus) => campus.id !== deleting.id));
      setSelectedCampusIds((current) => current.filter((campusId) => campusId !== deleting.id));
      toast.success("Data kampus dihapus.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal menghapus data kampus.");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="campus-manager">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="text-sm text-[#6B7280]">
            Kelola kampus serta rekening tujuan yang menjadi acuan pencocokan bukti transfer.
          </p>
          <p className="mt-1 text-xs font-semibold text-[#0B6B3A]" data-testid="campus-total">
            {campuses.length} kampus terdaftar
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => importRef.current?.click()}
            disabled={importing}
            data-testid="import-campuses-button"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-bold text-[#1F2937] transition-colors hover:bg-gray-50 disabled:opacity-60"
          >
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            {importing ? "Mengimpor..." : "Impor Excel"}
          </button>
          <button
            type="button"
            onClick={resetForm}
            data-testid="add-campus-button"
            className="inline-flex items-center gap-2 rounded-lg bg-[#27AE60] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0B6B3A]"
          >
            <Plus className="h-4 w-4" />
            Tambah Kampus
          </button>
          <button
            type="button"
            onClick={() => setBulkDeletion({ scope: "all", campusIds: [] })}
            disabled={campuses.length === 0}
            data-testid="open-delete-all-campuses"
            className="inline-flex items-center gap-2 rounded-lg border border-[#FECACA] bg-white px-4 py-2.5 text-sm font-bold text-[#DC2626] transition-colors hover:bg-[#FEF2F2] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" /> Hapus Semua
          </button>
          <input
            ref={importRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            data-testid="campus-import-input"
            onChange={(event) => importFile(event.target.files[0])}
          />
        </div>
      </div>

      <form
        onSubmit={submit}
        className="grid gap-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm md:grid-cols-2 xl:grid-cols-[1.3fr_0.9fr_1fr_1fr_1fr_auto]"
        data-testid="campus-form"
      >
        <input
          required
          value={form.name}
          onChange={(event) => setForm((previous) => ({ ...previous, name: event.target.value }))}
          placeholder="Nama kampus"
          data-testid="campus-name-input"
          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-[#27AE60] focus:ring-2 focus:ring-[#27AE60]/20"
        />
        <input
          value={form.bank_name}
          onChange={(event) => setForm((previous) => ({
            ...previous,
            bank_name: event.target.value,
          }))}
          placeholder="Nama bank"
          data-testid="campus-bank-name-input"
          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-[#27AE60] focus:ring-2 focus:ring-[#27AE60]/20"
        />
        <input
          value={form.bank_account_holder_name}
          onChange={(event) => setForm((previous) => ({
            ...previous,
            bank_account_holder_name: event.target.value,
          }))}
          placeholder="Atas nama rekening"
          data-testid="campus-account-holder-input"
          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-[#27AE60] focus:ring-2 focus:ring-[#27AE60]/20"
        />
        <input
          inputMode="numeric"
          value={form.bank_account_number}
          onChange={(event) => setForm((previous) => ({
            ...previous,
            bank_account_number: event.target.value,
          }))}
          placeholder="Nomor rekening kampus"
          data-testid="campus-account-number-input"
          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-[#27AE60] focus:ring-2 focus:ring-[#27AE60]/20"
        />
        <input
          value={form.code}
          onChange={(event) => setForm((previous) => ({ ...previous, code: event.target.value }))}
          placeholder="Kode kampus (opsional)"
          data-testid="campus-code-input"
          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-[#27AE60] focus:ring-2 focus:ring-[#27AE60]/20"
        />
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="save-campus-button"
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#0B6B3A] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#07532d] disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Tambah
          </button>
        </div>
      </form>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Cari nama atau kode kampus..."
          data-testid="campus-search-input"
          className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-[#27AE60] focus:ring-2 focus:ring-[#27AE60]/20"
        />
      </div>

      {selectedCampusIds.length > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 border border-[#FECACA] bg-[#FEF2F2] px-4 py-3"
          data-testid="selected-campuses-action-bar"
        >
          <p className="text-sm font-bold text-[#991B1B]" data-testid="selected-campuses-count">
            {selectedCampusIds.length} kampus dipilih
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelectedCampusIds([])}
              data-testid="clear-campus-selection"
              className="rounded-lg px-3 py-2 text-xs font-bold text-[#991B1B] transition-colors hover:bg-[#FECACA]"
            >
              Batalkan Pilihan
            </button>
            <button
              type="button"
              onClick={() => setBulkDeletion({ scope: "selected", campusIds: selectedCampusIds })}
              data-testid="open-selected-campus-deletion"
              className="inline-flex items-center gap-2 rounded-lg bg-[#DC2626] px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-[#B91C1C]"
            >
              <Trash2 className="h-4 w-4" /> Hapus Kampus Terpilih
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
        <table className="w-full min-w-[70rem] text-left" data-testid="campuses-table">
          <thead className="border-b border-gray-100 bg-[#F9FAFB] text-xs text-[#6B7280]">
            <tr>
              <th className="w-12 px-3 py-3">
                <input
                  type="checkbox"
                  checked={allVisibleCampusesSelected}
                  onChange={toggleAllCampusSelections}
                  disabled={sortedCampuses.length === 0}
                  data-testid="select-all-campuses"
                  aria-label="Pilih semua kampus yang terlihat"
                  className="h-4 w-4 accent-[#0B6B3A] disabled:cursor-not-allowed"
                />
              </th>
              <th className="w-16 px-5 py-3 font-semibold">No.</th>
              <CampusSortableHeader column="name" label="Nama Kampus" onSort={updateSort} sortConfig={sortConfig} />
              <CampusSortableHeader column="code" label="Kode Kampus" onSort={updateSort} sortConfig={sortConfig} />
              <CampusSortableHeader column="bank" label="Nama Bank" onSort={updateSort} sortConfig={sortConfig} />
              <CampusSortableHeader column="holder" label="Atas Nama Rekening" onSort={updateSort} sortConfig={sortConfig} />
              <CampusSortableHeader column="account" label="Nomor Rekening" onSort={updateSort} sortConfig={sortConfig} />
              <th className="px-5 py-3 text-right font-semibold">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="px-5 py-10 text-center" data-testid="campus-loading-state">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-[#27AE60]" />
                </td>
              </tr>
            ) : sortedCampuses.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-5 py-10 text-center text-sm text-[#6B7280]" data-testid="campus-empty-state">
                  Belum ada kampus yang sesuai.
                </td>
              </tr>
            ) : (
              sortedCampuses.map((campus, index) => {
                const isInlineEditing = editingCampusId === campus.id;
                const inlineInputClass = [
                  "w-full min-w-[9rem] rounded-md border border-[#8ED0A8] bg-white px-2 py-1.5",
                  "text-sm outline-none focus:border-[#27AE60] focus:ring-2 focus:ring-[#E8F6EE]",
                ].join(" ");
                return (
                  <tr
                    key={campus.id}
                    className="border-b border-gray-50 last:border-0"
                    data-testid={`campus-row-${campus.id}`}
                  >
                  <td className="px-3 py-3.5">
                    <input
                      type="checkbox"
                      checked={selectedCampusIds.includes(campus.id)}
                      onChange={() => toggleCampusSelection(campus.id)}
                      data-testid={`select-campus-${campus.id}`}
                      aria-label={`Pilih ${campus.name}`}
                      className="h-4 w-4 accent-[#0B6B3A]"
                    />
                  </td>
                  <td
                    className="px-5 py-3.5 text-sm font-semibold text-[#6B7280]"
                    data-testid={`campus-sequence-${campus.id}`}
                  >
                    {index + 1}
                  </td>
                  <td className="px-5 py-3.5 text-sm font-semibold text-[#1F2937]">
                    {isInlineEditing ? (
                      <input
                        value={inlineForm.name}
                        onChange={(event) => setInlineForm((current) => ({
                          ...current,
                          name: event.target.value,
                        }))}
                        data-testid={`inline-campus-name-${campus.id}`}
                        className={inlineInputClass}
                      />
                    ) : campus.name}
                  </td>
                  <td className="px-5 py-3.5 text-sm text-[#6B7280]">
                    {isInlineEditing ? (
                      <input
                        value={inlineForm.code}
                        onChange={(event) => setInlineForm((current) => ({
                          ...current,
                          code: event.target.value,
                        }))}
                        data-testid={`inline-campus-code-${campus.id}`}
                        className={inlineInputClass}
                      />
                    ) : campus.code || "—"}
                  </td>
                  <td className="px-5 py-3.5 text-sm text-[#6B7280]">
                    {isInlineEditing ? (
                      <input
                        value={inlineForm.bank_name}
                        onChange={(event) => setInlineForm((current) => ({
                          ...current,
                          bank_name: event.target.value,
                        }))}
                        data-testid={`inline-campus-bank-${campus.id}`}
                        className={inlineInputClass}
                      />
                    ) : campus.bank_name || "Belum dicatat"}
                  </td>
                  <td className="px-5 py-3.5 text-sm text-[#6B7280]">
                    {isInlineEditing ? (
                      <input
                        value={inlineForm.bank_account_holder_name}
                        onChange={(event) => setInlineForm((current) => ({
                          ...current,
                          bank_account_holder_name: event.target.value,
                        }))}
                        data-testid={`inline-campus-holder-${campus.id}`}
                        className={inlineInputClass}
                      />
                    ) : campus.bank_account_holder_name || "Belum dicatat"}
                  </td>
                  <td className="px-5 py-3.5 text-sm font-semibold text-[#0B6B3A]">
                    {isInlineEditing ? (
                      <input
                        inputMode="numeric"
                        value={inlineForm.bank_account_number}
                        onChange={(event) => setInlineForm((current) => ({
                          ...current,
                          bank_account_number: event.target.value,
                        }))}
                        placeholder={campus.bank_account_number_masked || "Nomor rekening"}
                        data-testid={`inline-campus-account-${campus.id}`}
                        className={inlineInputClass}
                      />
                    ) : campus.bank_account_number_masked || "Belum dicatat"}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex justify-end gap-1">
                      {isInlineEditing ? (
                        <>
                          <button
                            type="button"
                            title="Simpan perubahan kampus"
                            onClick={() => saveInlineEdit(campus)}
                            disabled={savingCampusId === campus.id}
                            data-testid={`save-inline-campus-${campus.id}`}
                            className="rounded-lg p-2 text-[#0B6B3A] hover:bg-[#E8F6EE] disabled:opacity-60"
                          >
                            {savingCampusId === campus.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            type="button"
                            title="Batal edit kampus"
                            onClick={cancelInlineEdit}
                            data-testid={`cancel-inline-campus-${campus.id}`}
                            className="rounded-lg p-2 text-[#6B7280] hover:bg-gray-100"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          title="Edit kampus langsung di baris"
                          onClick={() => startInlineEdit(campus)}
                          data-testid={`edit-campus-${campus.id}`}
                          className="rounded-lg p-2 text-[#0B6B3A] hover:bg-[#E8F6EE]"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        title="Hapus kampus"
                        onClick={() => setDeleting(campus)}
                        data-testid={`delete-campus-${campus.id}`}
                        className="rounded-lg p-2 text-[#DC2626] hover:bg-[#FEE2E2]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {deleting && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="delete-campus-dialog"
        >
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
            <Building2 className="h-8 w-8 text-[#DC2626]" />
            <h3 className="mt-3 font-display text-lg font-bold text-[#1F2937]">Hapus kampus?</h3>
            <p className="mt-2 text-sm text-[#6B7280]">
              {deleting.name} akan dihapus dari pilihan kampus mahasiswa.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleting(null)}
                data-testid="cancel-delete-campus-button"
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-bold text-[#1F2937] hover:bg-gray-50"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                data-testid="confirm-delete-campus-button"
                className="rounded-lg bg-[#DC2626] px-4 py-2 text-sm font-bold text-white hover:bg-[#B91C1C]"
              >
                Hapus
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkDeletion && (
        <CampusBulkDeletionDialog
          target={bulkDeletion}
          onClose={() => setBulkDeletion(null)}
          onDeleted={() => {
            setSelectedCampusIds([]);
            load();
          }}
        />
      )}
    </div>
  );
}